import { createReducer, on } from '@ngrx/store';
import { ILegalTexts, IMissingConsent, IUserData } from '../../../models';
import { ActionStateCreator, IActionState } from '../../../utils';
import * as fromActions from './user-profile.actions';
import { Consent } from '../../../models/account/consent.model';

export interface IUserProfileState {
  deleteUserActionState: IActionState;
  changePasswordActionState: IActionState;
  changeEmailActionState: IActionState;
  legalTexts: ILegalTexts;
  legalTextsActionState: IActionState;
  addRoommateActionState: IActionState;
  personalData: IUserData;
  personalDataActionState: IActionState;
  consent: Consent;
  consentActionState: IActionState;
  consentSetActionsState: IActionState;
  missingConsents: IMissingConsent[];
  hasMissingMandatoryConsent: boolean;
  missingConsentsActionState: IActionState;
}

export const initialState: IUserProfileState = {
  deleteUserActionState: ActionStateCreator.create(),
  changePasswordActionState: ActionStateCreator.create(),
  changeEmailActionState: ActionStateCreator.create(),
  legalTexts: null,
  legalTextsActionState: ActionStateCreator.create(),
  addRoommateActionState: ActionStateCreator.create(),
  personalData: null,
  personalDataActionState: ActionStateCreator.create(),
  consent: null,
  consentActionState: ActionStateCreator.create(),
  consentSetActionsState: ActionStateCreator.create(),
  missingConsents: null,
  hasMissingMandatoryConsent: null,
  missingConsentsActionState: ActionStateCreator.create(),
};

export const userProfileReducer = createReducer(
  initialState,

  on(fromActions.DeleteUser, state => {
    return {
      ...state,
      deleteUserActionState: ActionStateCreator.onStart(),
    };
  }),
  on(fromActions.DeleteUserSuccess, state => {
    return {
      ...state,
      deleteUserActionState: ActionStateCreator.onSuccess(),
    };
  }),
  on(fromActions.DeleteUserFailed, (state, { error }) => {
    return {
      ...state,
      deleteUserActionState: ActionStateCreator.onError(error),
    };
  }),

  on(fromActions.ChangePassword, state => {
    return {
      ...state,
      changePasswordActionState: ActionStateCreator.onStart(),
    };
  }),
  on(fromActions.ChangePasswordSuccess, state => {
    return {
      ...state,
      changePasswordActionState: ActionStateCreator.onSuccess(),
    };
  }),
  on(fromActions.ChangePasswordFailed, (state, { error }) => {
    return {
      ...state,
      changePasswordActionState: ActionStateCreator.onError(error),
    };
  }),

  on(fromActions.ChangeEmail, state => {
    return {
      ...state,
      changeEmailActionState: ActionStateCreator.onStart(),
    };
  }),
  on(fromActions.ChangeEmailSuccess, state => {
    return {
      ...state,
      changeEmailActionState: ActionStateCreator.onSuccess(),
    };
  }),
  on(fromActions.ChangeEmailFailed, (state, { error }) => {
    return {
      ...state,
      changeEmailActionState: ActionStateCreator.onError(error),
    };
  }),

  on(fromActions.LoadLegalTexts, state => {
    return {
      ...state,
      legalTextsActionState: ActionStateCreator.onStart(),
    };
  }),
  on(fromActions.LoadLegalTextsSuccess, (state, { legalTexts }) => {
    return {
      ...state,
      legalTexts,
      legalTextsActionState: ActionStateCreator.onSuccess(),
    };
  }),
  on(fromActions.LoadLegalTextsFailed, (state, { error }) => {
    return {
      ...state,
      legalTextsActionState: ActionStateCreator.onError(error),
    };
  }),

  on(fromActions.LoadPersonalData, state => {
    return {
      ...state,
      personalDataActionState: ActionStateCreator.onStart(),
    };
  }),
  on(fromActions.LoadPersonalDataSuccess, (state, { personalData }) => {
    return {
      ...state,
      personalData,
      personalDataActionState: ActionStateCreator.onSuccess(),
    };
  }),
  on(fromActions.LoadPersonalDataFailed, (state, { error }) => {
    return {
      ...state,
      personalDataActionState: ActionStateCreator.onError(error),
    };
  }),
  on(fromActions.GetConsent, state => {
    return {
      ...state,
      consentActionState: ActionStateCreator.onStart(),
    };
  }),
  on(fromActions.GetConsentSuccess, (state, { response, consentType }) => {
    const consent: Consent = {
      ...(state.consent || {}),
      [consentType]: response,
    };

    return {
      ...state,
      consent,
      consentActionState: ActionStateCreator.onSuccess(),
    };
  }),
  on(fromActions.GetConsentFailed, (state, { error }) => {
    return {
      ...state,
      consentActionState: ActionStateCreator.onError(error),
    };
  }),
  on(fromActions.SetConsent, state => {
    return {
      ...state,
      consentSetActionsState: ActionStateCreator.onStart(),
    };
  }),
  on(fromActions.SetConsentSuccess, (state, { response, consentType }) => {
    const consent: Consent = {
      ...(state.consent || {}),
      [consentType]: {
        ...(state.consent || {})[consentType],
        acceptedLatest: response,
      },
    };

    return {
      ...state,
      consent,
      consentSetActionsState: ActionStateCreator.onSuccess(),
    };
  }),
  on(fromActions.SetConsentFailed, (state, { error }) => {
    return {
      ...state,
      consentSetActionsState: ActionStateCreator.onError(error),
    };
  }),
  on(fromActions.SetConsentV2, state => {
    return {
      ...state,
      consentSetActionsState: ActionStateCreator.onStart(),
    };
  }),
  on(fromActions.SetConsentV2Success, (state, { legalTextId }) => {
    const missingConsents: IMissingConsent[] = state.missingConsents?.filter(
      c => c.legalTextId !== legalTextId
    );
    const hasMissingMandatoryConsent: boolean =
      missingConsents?.filter(c => c.mandatory).length > 0 || false;

    return {
      ...state,
      missingConsents,
      hasMissingMandatoryConsent,
      consentSetActionsState: ActionStateCreator.onSuccess(),
    };
  }),
  on(fromActions.SetConsentV2Failed, (state, { error }) => {
    return {
      ...state,
      consentSetActionsState: ActionStateCreator.onError(error),
    };
  }),
  on(fromActions.SetHasMissingMandatoryConsent, (state, { hasMissingMandatoryConsent }) => {
    return {
      ...state,
      hasMissingMandatoryConsent,
    };
  }),
  on(fromActions.LoadMissingConsents, state => ({
    ...state,
    missingConsentsActionState: ActionStateCreator.onStart(),
  })),
  on(fromActions.LoadMissingConsentsSuccess, (state, { missingConsents }) => {
    // Only count MANDATORY consents - non-mandatory ones don't block data loading
    const hasMissingMandatoryConsent: boolean =
      missingConsents?.filter(c => c.mandatory).length > 0;

    return {
      ...state,
      missingConsents,
      hasMissingMandatoryConsent,
      missingConsentsActionState: ActionStateCreator.onSuccess(),
    };
  }),
  on(fromActions.LoadMissingConsentsFailed, (state, { error }) => {
    return {
      ...state,
      missingConsentsActionState: ActionStateCreator.onError(error),
    };
  })
);
